<template>
  <DModal
    :show-close="false"
    :isBgOverlay="true"
    @close="onClose"
    :appendToBody="true"
    style="z-index: 12312312312312"
  >
    <div class="d-modal" slot="insertComponent" @click.stop>
      <div class="d-modal__header">
        <div>
          <h2>Opret ansættelse</h2>
          <p class="d-modal__subtitle">{{ user.name }}</p>
        </div>
        <div class="font-large icon__bg-circle d-modal__header__close" @click="onClose">
          <span class="icon-cross2"></span>
        </div>
      </div>

      <!-- Trin 1: Hent ansættelsen fra lønsystemet -->
      <template v-if="step === 1">
        <div class="d-modal__content">
          <p class="text-explainer">
            Ansættelsen hentes fra lønsystemet. Indtast det {{ numberLabel.toLowerCase() }},
            som ansættelsen har i lønsystemet, så importeres stilling, periode og
            beskæftigelsesgrad automatisk.
          </p>

          <div v-if="needsCpr" class="d-input-group">
            <label class="d-label">CPR-nummer</label>
            <input
              type="text"
              class="d-input"
              v-model="cpr"
              ref="cpr_input"
              placeholder="ddmmåå-nnnn"
              inputmode="numeric"
              maxlength="11"
              :class="{'is-invalid': !!cprError}"
              @input="errorMessage = ''"
              @keydown.enter="onFetch"
            />
            <p v-if="cprError" class="text-explainer text-error marg-05-top">{{ cprError }}</p>
          </div>

          <div class="d-input-group">
            <label class="d-label">
              {{ numberLabel }}
              <span v-if="maxLength" style="font-size: 7px">(max {{ maxLength }} cifre)</span>
            </label>
            <input
              type="text"
              v-model="code"
              ref="number_input"
              class="d-input"
              :maxlength="maxLength || 20"
              :class="{'is-invalid': !!codeError || !!errorMessage}"
              @input="onCodeInput"
              @keydown.enter="onFetch"
            />
            <p v-if="codeError" class="text-explainer text-error marg-05-top">{{ codeError }}</p>
            <p v-else-if="errorMessage" class="text-explainer text-error marg-05-top">{{ errorMessage }}</p>
          </div>
        </div>

        <div class="d-modal__buttons">
          <div></div>
          <div>
            <button class="do-bttn" @click="onClose">
              {{ $t('vue2.common.cancel') }}
            </button>
            <button
              class="do-bttn do-bttn--submit"
              :class="{
                'do-bttn--submit--disabled': !isValid || isSubmitting,
                'do-bttn--submit--loading': isSubmitting
              }"
              @click="onFetch"
            >
              Hent ansættelse
            </button>
          </div>
        </div>
      </template>

      <!-- Trin 2: Ansættelsen er hentet - tildel stillingskategori -->
      <template v-else>
        <div class="d-modal__content">
          <div class="import-success">
            <p class="import-success__title">Ansættelsen er hentet fra lønsystemet</p>
            <p v-for="employment in importedSchoolEmployments" :key="employment.id" class="import-success__employment">
              <strong>{{ employment.employment_number }} - {{ employment.source_title || employment.title }}</strong><br>
              {{ formatDate(employment.start_date) }} – {{ formatDate(employment.end_date) }}
              <template v-if="employment.employment_percent"> · {{ formatPercent(employment.employment_percent) }}%</template>
              <template v-if="employment.organisation"> · {{ employment.organisation }}</template>
            </p>
          </div>

          <div class="d-input-group">
            <label class="d-label">Stillingskategori</label>
            <select v-model="employmentTemplateId" class="do-select">
              <option :value="null" disabled>Vælg stillingskategori</option>
              <option v-for="template in employmentTemplates" :key="template.id" :value="template.id">
                {{ template.title }}
              </option>
            </select>
            <p v-if="errorMessage" class="text-explainer text-error marg-05-top">{{ errorMessage }}</p>
          </div>
        </div>

        <div class="d-modal__buttons">
          <div></div>
          <div>
            <button class="do-bttn" @click="onSkipTemplate">
              Spring over
            </button>
            <button
              class="do-bttn do-bttn--submit"
              :class="{
                'do-bttn--submit--disabled': !employmentTemplateId || isSubmitting,
                'do-bttn--submit--loading': isSubmitting
              }"
              @click="onAssignTemplate"
            >
              Gem stillingskategori
            </button>
          </div>
        </div>
      </template>
    </div>
  </DModal>
</template>

<script>
import DModal from '../../common/DModal.vue'
import docendo from '../../../docendo'
import eventbus from '../../../eventbus'

// Opretter en ansættelse i to trin: Først hentes ansættelsen fra lønkilden
// (SD eller KMD OPUS) ud fra det nummer, brugeren indtaster - backend slår
// nummeret op på medarbejderens CPR og importerer kun den ene ansættelse.
// Derefter forbliver modalen åben, så der kan tildeles en stillingskategori.
export default {
  name: 'EmploymentImportModal',
  components: {DModal},
  props: {
    user: {
      type: Object,
      required: true
    },
    // 'Tjenestenummer' (SD) eller 'Medarbejdernummer' (KMD OPUS)
    numberLabel: {
      type: String,
      default: 'Ansættelsesnummer'
    },
    maxLength: {
      type: Number,
      default: null
    },
    // KMD OPUS kender numrene zero-paddet (PERNR, fx '00098172'), og backenden
    // sammenligner eksakt - så nummeret paddes til denne længde inden afsendelse.
    padLength: {
      type: Number,
      default: null
    }
  },
  data () {
    return {
      step: 1,
      code: '',
      cpr: '',
      cprSaved: false,
      importedSchoolEmployments: [],
      employmentTemplateId: null,
      isSubmitting: false,
      errorMessage: ''
    }
  },
  computed: {
    schoolId () {
      return this.$store.getters.schoolId
    },
    employmentTemplates () {
      return this.$store.getters.employmentTemplatesSorted
    },
    // Brugeren mangler CPR - det indtastes og gemmes sammen med importen.
    needsCpr () {
      return !this.user.ssn && !this.cprSaved
    },
    cprDigits () {
      return String(this.cpr).replace(/\D+/g, '')
    },
    cprError () {
      if (this.cprDigits.length === 0) return ''
      if (this.cprDigits.length !== 10) return 'CPR-nummer skal være 10 cifre'
      return ''
    },
    // Nummeret som det sendes til backenden - zero-paddet hvis lønsystemet kræver det.
    numberToSend () {
      const val = String(this.code).trim()
      return this.padLength ? val.padStart(this.padLength, '0') : val
    },
    codeError () {
      const val = String(this.code).trim()
      if (val.length === 0) return ''
      if (!/^\d+$/.test(val)) return 'Kun cifre er tilladt'
      if (this.maxLength && val.length > this.maxLength) return 'Maks ' + this.maxLength + ' cifre'
      return ''
    },
    isValid () {
      const val = String(this.code).trim()
      const codeOk = val.length > 0 && !this.codeError
      if (this.needsCpr) {
        return codeOk && this.cprDigits.length === 10
      }
      return codeOk
    }
  },
  methods: {
    onCodeInput (e) {
      const max = this.maxLength || 20
      this.code = String(e.target.value).replace(/\D+/g, '').slice(0, max)
      this.errorMessage = ''
    },
    formatDate (date) {
      if (!date) return ''
      const parts = String(date).split('-')
      if (parts.length !== 3) return date
      return parts[2] + '-' + parts[1] + '-' + parts[0]
    },
    formatPercent (percent) {
      const number = parseFloat(percent)
      if (isNaN(number)) return percent
      return number % 1 === 0 ? String(Math.round(number)) : String(number)
    },
    // Trin 1: Hent ansættelsen (gem evt. CPR først).
    onFetch () {
      if (!this.isValid || this.isSubmitting) return
      this.isSubmitting = true
      this.errorMessage = ''
      if (this.needsCpr) {
        this.saveCprThenImport()
      } else {
        this.importEmployment()
      }
    },
    // Backend-fejl (fx ukendt nummer) kommer gennem succes-callbacken som
    // {status: 'error', message: ...} - samme konvention som resten af appen.
    resultError (result) {
      return result && result.status === 'error' ? (result.message || 'Der skete en fejl') : null
    },
    // Oversætter kendte backend-fejl til dansk. Ukendte fejl vises som de er.
    friendlyError (message) {
      if (/knows no employment/.test(message)) {
        return 'Lønsystemet kender ikke nummeret ' + this.numberToSend + ' på denne medarbejders CPR-nummer'
      }
      return message
    },
    // Gemmer CPR på brugeren først - backendens import slår op i lønsystemet
    // på brugerens gemte CPR, så det skal være på plads inden importen.
    saveCprThenImport () {
      docendo.updateUsers(this.schoolId, this.user.id, {ssn: this.cprDigits}, result => {
        if (this.resultError(result)) {
          this.isSubmitting = false
          this.errorMessage = 'CPR-nummeret kunne ikke gemmes - det er muligvis allerede brugt'
          return
        }
        this.cprSaved = true
        // Genhent brugere, så CPR vises i listen - også hvis importen skulle fejle.
        this.$store.dispatch('getUsers')
        this.importEmployment()
      }, failure => {
        this.isSubmitting = false
        this.errorMessage = 'CPR-nummeret kunne ikke gemmes - det er muligvis allerede brugt'
      })
    },
    importEmployment () {
      docendo.importEmployment(
        this.schoolId,
        this.user.id,
        this.numberToSend,
        // Succes-callbacken modtager event.data direkte: et array af skolens
        // ansættelser (school_employments) på det importerede nummer - eller
        // et fejlobjekt, hvis backenden afviste importen.
        result => {
          this.isSubmitting = false
          const error = this.resultError(result)
          if (error) {
            this.errorMessage = this.friendlyError(error)
            return
          }
          this.importedSchoolEmployments = Array.isArray(result) ? result : []
          // Vis den nye ansættelse i tabellen bag modalen med det samme.
          this.$store.dispatch('readEmployments')
          this.$emit('imported')
          this.errorMessage = ''
          this.step = 2
        },
        failure => {
          this.isSubmitting = false
          this.errorMessage = failure && failure.error
            ? failure.error
            : 'Kunne ikke hente ansættelsen fra lønsystemet'
        }
      )
    },
    // Trin 2: Tildel stillingskategori til den hentede ansættelse.
    onAssignTemplate () {
      if (!this.employmentTemplateId || this.isSubmitting) return
      const ids = this.importedSchoolEmployments.map(employment => employment.id).filter(id => id != null)
      if (ids.length === 0) {
        this.finish()
        return
      }
      this.isSubmitting = true
      this.errorMessage = ''
      docendo.assignEmploymentTemplateToEmployment(this.schoolId, this.employmentTemplateId, ids, result => {
        this.isSubmitting = false
        if (this.resultError(result)) {
          this.errorMessage = 'Stillingskategorien kunne ikke tildeles - prøv igen'
          return
        }
        this.finish()
      }, failure => {
        this.isSubmitting = false
        this.errorMessage = 'Stillingskategorien kunne ikke tildeles - prøv igen'
      })
    },
    onSkipTemplate () {
      this.finish()
    },
    finish () {
      this.$store.dispatch('readEmployments')
      eventbus.toast = {text: 'Ansættelsen er hentet fra lønsystemet', success: true, position: 'bottomLeft'}
      this.$emit('close')
    },
    onClose () {
      // Ansættelsen er allerede importeret på trin 2 - luk uden kategori er ok.
      this.$emit('close')
    }
  },
  mounted () {
    this.$nextTick(_ => {
      const el = this.needsCpr ? this.$refs.cpr_input : this.$refs.number_input
      if (el) el.focus()
    })
  }
}
</script>

<style scoped>
@import "../../../assets/css/variables.css";

.d-modal {
  width: 500px;
  border-radius: 16px;
  position: relative;
}

.d-modal__header {
  margin-bottom: 0;
  text-align: left;
  display: flex;
  justify-content: space-between;
}

.d-modal__subtitle {
  margin: 0.25rem 0 0 0;
  font-size: 12px;
  opacity: 0.7;
}

.d-modal__content {
  padding: 1rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.d-modal__buttons {
  display: flex;
  justify-content: space-between;
  padding: 1rem 2rem;
}

.d-modal__buttons > div {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.d-input-group {
  flex: 1;
  padding: 0;
  margin: 0;
}

.d-input-group .d-input,
.d-input-group .do-select {
  height: 34px;
  width: 100%;
  margin-top: 0.25rem;
}

.d-input-group .do-select {
  border: 1px solid #edf2f7;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, .1), 0 1px 2px -1px rgba(0, 0, 0, .1);
}

.d-input.is-invalid {
  border-color: #e53e3e;
  box-shadow: 0 0 0 1px rgba(229, 62, 62, .2);
}

.text-error {
  color: #e53e3e;
}

.import-success {
  background: #f0faf4;
  border: 1px solid #b7e4c7;
  border-radius: 4px;
  padding: 0.75rem;
}

.import-success__title {
  margin: 0 0 0.5rem 0;
  font-weight: 600;
  color: #2d6a4f;
  font-size: 12px;
}

.import-success__employment {
  margin: 0;
  font-size: 12px;
  line-height: 1.5;
}
</style>



// WEBPACK FOOTER //
// EmploymentImportModal.vue?7e3e61fa